/**
 * Isomorphic (browser + Node) helpers for the `exp` claim of Aegis session access tokens.
 * Aegis mints short-lived tokens (~10 min, see AegisSessionTokenService), so anything that
 * replays a token into another context — notably headless PDF rendering — must know how much
 * life is left before it starts issuing requests.
 */

function decodeBase64Url(segment: string): string | null {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
  try {
    if (typeof atob === 'function') {
      return atob(padded)
    }
  } catch {
    return null
  }
  try {
    const nodeBuffer = (globalThis as { Buffer?: { from: (s: string, enc: string) => { toString: (enc: string) => string } } })
      .Buffer
    if (nodeBuffer) {
      return nodeBuffer.from(padded, 'base64').toString('utf8')
    }
  } catch {
    return null
  }
  return null
}

/** Epoch ms of the token's `exp` claim, or null when the token is unreadable. */
export function decodeAccessTokenExpiryMs(accessToken: string | null | undefined): number | null {
  if (!accessToken || typeof accessToken !== 'string') return null
  const parts = accessToken.split('.')
  if (parts.length < 2 || !parts[1]) return null
  const json = decodeBase64Url(parts[1])
  if (!json) return null
  try {
    const payload = JSON.parse(json) as { exp?: unknown }
    const exp = typeof payload.exp === 'number' ? payload.exp : Number(payload.exp)
    if (!Number.isFinite(exp) || exp <= 0) return null
    return exp * 1000
  } catch {
    return null
  }
}

/** Remaining lifetime in ms; null when the expiry cannot be determined. */
export function accessTokenTtlMs(accessToken: string | null | undefined, now: number = Date.now()): number | null {
  const expMs = decodeAccessTokenExpiryMs(accessToken)
  if (expMs == null) return null
  return expMs - now
}

/** Whole seconds of remaining lifetime, for logs and telemetry. */
export function accessTokenTtlSeconds(accessToken: string | null | undefined, now: number = Date.now()): number | null {
  const ttl = accessTokenTtlMs(accessToken, now)
  return ttl == null ? null : Math.round(ttl / 1000)
}

/**
 * Undecodable tokens are treated as usable: only Aegis can rule on them, and refusing to send
 * an opaque token would break every caller that works today.
 */
export function isAccessTokenExpired(
  accessToken: string | null | undefined,
  now: number = Date.now()
): boolean {
  const ttl = accessTokenTtlMs(accessToken, now)
  if (ttl == null) return false
  return ttl <= 0
}

/** True when the token has less than `minTtlMs` left (or is already expired). */
export function accessTokenNeedsRefresh(
  accessToken: string | null | undefined,
  minTtlMs: number,
  now: number = Date.now()
): boolean {
  const ttl = accessTokenTtlMs(accessToken, now)
  if (ttl == null) return false
  return ttl < minTtlMs
}

/** Parse the `authToken` JSON blob stored by the app (`{ access_token, token_type }`). */
export function parseAccessTokenFromAuthJson(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) return null
  try {
    const parsed = JSON.parse(raw) as { access_token?: unknown }
    const token = typeof parsed?.access_token === 'string' ? parsed.access_token.trim() : ''
    return token.length > 0 ? token : null
  } catch {
    return null
  }
}
